import { authApi, useRefreshTokenMutation } from 'api/AuthApi';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Outlet } from 'react-router-dom';
import { selectAuthToken, setAccessToken } from 'store/authSlice';
import { RootState, useAppDispatch } from 'store/store';
import { getCurrentUrl } from 'utils/utils';

const PrivateRoutes = () => {
  const dispatch = useAppDispatch();
  const accessToken = useSelector(selectAuthToken);
  const [refreshToken] = useRefreshTokenMutation();
  const [isCheckingAuth, setIsCheckingAuth] = useState(!accessToken);
  const [sessionRestoreAttempted, setSessionRestoreAttempted] = useState(Boolean(accessToken));
  const meQueryState = useSelector((state: RootState) => authApi.endpoints.me.select()(state));
  const hasResolvedAuthUser = Boolean(meQueryState.data);
  const isMeQueryPending = meQueryState.isUninitialized || (meQueryState.isLoading && !hasResolvedAuthUser);

  useEffect(() => {
    let isCancelled = false;

    const restoreSession = async () => {
      if (accessToken) {
        if (!isCancelled) {
          setSessionRestoreAttempted(true);
          setIsCheckingAuth(false);
        }
        return;
      }

      if (sessionRestoreAttempted) {
        if (!isCancelled) {
          setIsCheckingAuth(false);
        }
        return;
      }

      if (!isCancelled) {
        setIsCheckingAuth(true);
      }

      try {
        const restoredAccessToken = await refreshToken().unwrap();
        if (!isCancelled && restoredAccessToken) {
          dispatch(setAccessToken(restoredAccessToken));
        }
      } catch (_) {
        // Missing/expired refresh cookie is an expected unauthenticated boot path.
      } finally {
        if (!isCancelled) {
          setSessionRestoreAttempted(true);
          setIsCheckingAuth(false);
        }
      }
    };

    restoreSession();

    return () => {
      isCancelled = true;
    };
  }, [accessToken, dispatch, refreshToken, sessionRestoreAttempted]);

  if (isCheckingAuth) {
    return null;
  }

  if (!accessToken) {
    return <Navigate to="/login" state={{ redirectUrl: getCurrentUrl() }} replace />;
  }

  if (isMeQueryPending) {
    return null;
  }

  if (!hasResolvedAuthUser) {
    return <Navigate to="/login" state={{ redirectUrl: getCurrentUrl() }} replace />;
  }

  return <Outlet />;
};

export default PrivateRoutes;
